import type { GaugeUrlParams, ProposalUrlParams, UserUrlParams } from '@/dao/types/dao.types'
import { createRoute, lazyRouteComponent } from '@tanstack/react-router'
import { ETHEREUM_ONLY_NETWORK_SWITCH } from '@ui/features/layout/switch-chain/switch-policies'
import { redirectTo } from '@ui/hooks/router'
import { rootRoute } from './root.routes'
import { createSharedRoutes } from './shared.routes'

const DaoLayout = lazyRouteComponent(() => import('@/dao/DaoLayout'), 'DaoLayout')
const PageAnalytics = lazyRouteComponent(() => import('@/dao/components/PageAnalytics'), 'Analytics')
const PageGauges = lazyRouteComponent(() => import('@/dao/components/PageGauges'), 'Gauges')
const PageGauge = lazyRouteComponent(() => import('@/dao/components/PageGauge'), 'Gauge')
const PageProposals = lazyRouteComponent(() => import('@/dao/components/PageProposals'), 'Proposals')
const PageProposal = lazyRouteComponent(() => import('@/dao/components/PageProposal'), 'Proposal')
const PageUser = lazyRouteComponent(() => import('@/dao/components/PageUser'), 'User')
const PageVeCrv = lazyRouteComponent(() => import('@/dao/components/PageVeCrv'), 'VeCrv')

const daoLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'dao',
  component: DaoLayout,
  staticData: { networkSwitch: ETHEREUM_ONLY_NETWORK_SWITCH },
})

const layoutProps = { getParentRoute: () => daoLayoutRoute }

export const daoRoutes = daoLayoutRoute.addChildren([
  ...createSharedRoutes('dao', layoutProps),
  createRoute({
    path: '$network',
    loader: ({ params: { network } }) => redirectTo(`/dao/${network}/proposals/`),
    ...layoutProps,
  }),
  createRoute({
    path: '$network/analytics',
    component: PageAnalytics,
    head: () => ({ meta: [{ title: 'Analytics - Curve' }] }),
    ...layoutProps,
  }),
  createRoute({
    path: '$network/gauges',
    component: PageGauges,
    head: () => ({ meta: [{ title: 'Gauges - Curve' }] }),
    ...layoutProps,
  }),
  createRoute({
    path: '$network/gauges/$gaugeAddress',
    component: PageGauge,
    head: ({ params: { gaugeAddress } }: { params: GaugeUrlParams }) => ({
      meta: [{ title: `Gauge - ${gaugeAddress} - Curve` }],
    }),
    ...layoutProps,
  }),
  createRoute({
    path: '$network/proposals',
    component: PageProposals,
    head: () => ({ meta: [{ title: 'Proposals - Curve' }] }),
    ...layoutProps,
  }),
  createRoute({
    path: '$network/proposals/$proposalId',
    component: PageProposal,
    head: ({ params: { proposalId } }: { params: ProposalUrlParams }) => ({
      meta: [{ title: `Proposal - ${proposalId} - Curve` }],
    }),
    ...layoutProps,
  }),
  createRoute({
    path: '$network/user/$userAddress',
    component: PageUser,
    head: ({ params: { userAddress } }: { params: UserUrlParams }) => ({
      meta: [{ title: `veCRV Holder - ${userAddress} - Curve` }],
    }),
    ...layoutProps,
  }),
  createRoute({
    path: '$network/vecrv',
    component: PageVeCrv,
    head: () => ({ meta: [{ title: 'CRV Locker - Curve' }] }),
    ...layoutProps,
  }),
  createRoute({
    path: '$network/vecrv/$formType',
    loader: ({ params: { network } }) => redirectTo(`/dao/${network}/vecrv`),
    ...layoutProps,
  }),
])
